import {
  BaseRepository,
  IBaseModel,
  IListFetchParams,
  MN_JOBS,
  MN_ORDERS,
  MN_RESOURCES,
  MN_SUPPLIERS,
  MN_TYPE_OF_PAYMENTS,
  MODULE,
} from '../common';
import type { JobState, OrderType, OrderState, Prisma } from '@prisma/client';
import {
  OrderStateEnum,
  OrderTypeEnum,
} from '@jobcost-monorepo/database/prisma/enums';
import * as z from 'zod';
import type { KeyValue } from '@jobcost-monorepo/utils';
import DailyCost from '../ui/CustomComponents/DailyCost';
import DailyRevenue from '../ui/CustomComponents/DailyRevenue';
import HookComponentOrder from '../ui/CustomHookComponent/HookComponentOrder';
import CreateOrderDocButton from '../ui/CustomComponents/CreateOrderDocButton';
import CreateOrderDuplicateButton from '../ui/CustomComponents/CreateOrderDuplicateButton';
import { util } from 'zod';
import Omit = util.Omit;
import GenerateOrderCodeButton from '../ui/CustomComponents/GenerateOrderCodeButton';

type Order = Prisma.$OrderPayload['scalars'];

export type IOrder = Order &
  Omit<IBaseModel, 'createdAt' | 'updatedAt' | 'id'> & {
    resource: KeyValue;
    supplier: KeyValue;
    job: KeyValue;
    typeOfPayment: KeyValue;
    typeOfPaymentSupplier: KeyValue;
  };

export interface IListOrderFetchParams extends IListFetchParams {
  jobId?: number;
  resourceId?: number;
  supplierId?: number;
  startDate?: string;
  endDate?: string;
  orderType?: OrderType;
  orderState?: OrderState;
  isExpired?: boolean;
  isExpiring?: boolean;
  jobState?: JobState;
  excludeRevenue?: boolean;
}

class OrderRepository extends BaseRepository<IOrder, IListOrderFetchParams> {
  constructor(moduleName: MODULE) {
    super(moduleName);
    this.keyValueMapFields = {
      idlinkedField: 'id',
      descriptionlinkedField: ['code', 'description'],
    };
    this.gridFieldsOptions = [
      {
        visible: true,
        position: 0,
        linkedField: 'id',
        type: '',
        align: 'left',
        width: '12rem',
        sortable: false,
        formatType: 'string',
      },
      {
        visible: true,
        position: 1,
        linkedField: 'code',
        type: '',
        align: 'left',
        width: 'w-full',
        sortable: false,
        formatType: 'string',
      },
      {
        visible: true,
        position: 2,
        linkedField: 'description',
        type: '',
        align: 'left',
        width: 'w-full',
        sortable: false,
        formatType: 'string',
      },
      {
        visible: true,
        position: 3,
        linkedField: 'job.name',
        type: '',
        align: 'left',
        width: 'w-full',
        sortable: false,
        formatType: 'string',
      },
      {
        visible: true,
        position: 4,
        linkedField: 'startDate',
        type: '',
        align: 'left',
        width: 'w-full',
        sortable: false,
        formatType: 'date',
      },
      {
        visible: true,
        position: 5,
        linkedField: 'endDate',
        type: '',
        align: 'left',
        width: 'w-full',
        sortable: false,
        formatType: 'date',
      },
      {
        visible: true,
        position: 6,
        linkedField: 'orderState',
        type: '',
        align: 'left',
        width: 'w-full',
        sortable: false,
        formatType: 'enum',
        translationModuleName: MN_ORDERS,
        enumValues: OrderStateEnum,
      },
      {
        visible: true,
        position: 8,
        linkedField: 'supplier.name',
        type: '',
        align: 'left',
        width: 'w-full',
        sortable: false,
        formatType: 'string',
      },
      {
        visible: true,
        position: 9,
        linkedField: 'technology',
        type: '',
        align: 'left',
        width: 'w-full',
        sortable: false,
        formatType: 'string',
      },
      {
        visible: true,
        position: 10,
        linkedField: 'orderType',
        type: '',
        align: 'left',
        width: 'w-full',
        sortable: false,
        formatType: 'enum',
        translationModuleName: MN_ORDERS,
        enumValues: OrderTypeEnum,
      },
    ];

    this.gridFiltersOptions = [
      {
        position: { x: 0, y: 0 },
        linkedField: 'jobId',
        width: 'w-full',
        formatType: 'dropdownWithSearch',
        moduleName: MN_JOBS,
        translationModuleName: MN_ORDERS,
      },
      {
        position: { x: 0, y: 1 },
        linkedField: 'resourceId',
        width: 'w-full',
        formatType: 'dropdownWithSearch',
        moduleName: MN_RESOURCES,
        translationModuleName: MN_RESOURCES,
      },
      {
        position: { x: 0, y: 2 },
        linkedField: 'supplierId',
        width: 'w-full',
        formatType: 'dropdownWithSearch',
        moduleName: MN_SUPPLIERS,
        translationModuleName: MN_SUPPLIERS,
      },
      {
        position: { x: 0, y: 3 },
        linkedField: 'startDate',
        width: 'w-full',
        formatType: 'date',
        moduleName: MN_ORDERS,
        translationModuleName: MN_ORDERS,
      },
      {
        position: { x: 1, y: 3 },
        linkedField: 'endDate',
        width: 'w-full',
        formatType: 'date',
        moduleName: MN_ORDERS,
        translationModuleName: MN_ORDERS,
      },
      {
        position: { x: 0, y: 4 },
        linkedField: 'orderType',
        width: 'w-full',
        formatType: 'dropdown',
        moduleName: MN_ORDERS,
        translationModuleName: MN_ORDERS,
        enumValues: OrderTypeEnum,
      },
      {
        position: { x: 0, y: 5 },
        linkedField: 'orderState',
        width: 'w-full',
        formatType: 'dropdown',
        moduleName: MN_ORDERS,
        translationModuleName: MN_ORDERS,
        enumValues: OrderStateEnum,
      },
      {
        position: { x: 0, y: 6 },
        linkedField: 'excludeRevenue',
        width: 'w-full',
        formatType: 'boolean',
        moduleName: MN_ORDERS,
        translationModuleName: MN_ORDERS,
      },
    ];

    this.formFieldsOptions = [
      {
        visible: false,
        readOnly: true,
        position: { x: 0, y: 0 },
        linkedField: 'id',
        type: 'string',
        width: '12rem',
        formatType: 'input',
        moduleName: MN_ORDERS,
        translationModuleName: MN_ORDERS,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 0, y: 0 },
        linkedField: 'jobId',
        linkedFieldForDefaultValue: 'job.name',
        type: 'number',
        width: 'w-full',
        formatType: 'dropdownWithSearch',
        moduleName: MN_JOBS,
        translationModuleName: MN_ORDERS,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 1, y: 0 },
        linkedField: 'typeOfPaymentId',
        linkedFieldForDefaultValue: 'typeOfPayment.name',
        type: 'number',
        width: 'w-full',
        formatType: 'dropdownWithSearch',
        moduleName: MN_TYPE_OF_PAYMENTS,
        translationModuleName: MN_ORDERS,
        dependsOnFields: ['jobId'],
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 0, y: 2 },
        linkedField: 'startDate',
        type: 'date',
        width: 'w-full',
        formatType: 'date',
        moduleName: MN_ORDERS,
        translationModuleName: MN_ORDERS,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 1, y: 2 },
        linkedField: 'endDate',
        type: 'date',
        width: 'w-full',
        formatType: 'date',
        moduleName: MN_ORDERS,
        translationModuleName: MN_ORDERS,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 0, y: 3 },
        linkedField: 'code',
        type: 'string',
        width: 'w-full',
        formatType: 'input',
        moduleName: MN_ORDERS,
        translationModuleName: MN_ORDERS,
      },
      {
        visible: true,
        readOnly: false,
        linkedField: 'GenerateOrderCodeButton',
        position: { x: 1, y: 3 },
        formatType: 'custom',
        customComponent: GenerateOrderCodeButton,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 2, y: 3 },
        linkedField: 'description',
        type: 'string',
        width: 'w-full',
        formatType: 'input',
        moduleName: MN_ORDERS,
        translationModuleName: MN_ORDERS,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 0, y: 4 },
        linkedField: 'orderType',
        type: 'string',
        width: 'w-full',
        formatType: 'dropdown',
        enumValues: OrderTypeEnum,
        moduleName: MN_ORDERS,
        translationModuleName: MN_ORDERS,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 1, y: 4 },
        linkedField: 'fixedCost',
        type: 'number',
        width: 'w-full',
        formatType: 'currency',
        moduleName: MN_ORDERS,
        translationModuleName: MN_ORDERS,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 0, y: 5 },
        linkedField: 'resourceId',
        linkedFieldForDefaultValue: 'resource.name',
        type: 'number',
        width: 'w-full',
        formatType: 'dropdownWithSearch',
        moduleName: MN_RESOURCES,
        translationModuleName: MN_ORDERS,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 1, y: 5 },
        linkedField: 'hoursAllocatedPerDay',
        type: 'number',
        width: 'w-full',
        formatType: 'input',
        moduleName: MN_ORDERS,
        translationModuleName: MN_ORDERS,
        dependsOnFields: ['resourceId'],
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 0, y: 6 },
        linkedField: 'hourCost',
        type: 'number',
        width: 'w-full',
        formatType: 'currency',
        moduleName: MN_ORDERS,
        translationModuleName: MN_ORDERS,
        dependsOnFields: ['resourceId'],
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 1, y: 6 },
        linkedField: 'dailyCost',
        formatType: 'custom',
        customComponent: DailyCost,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 2, y: 6 },
        linkedField: 'hourRevenue',
        type: 'number',
        width: 'w-full',
        formatType: 'currency',
        moduleName: MN_ORDERS,
        translationModuleName: MN_ORDERS,
        dependsOnFields: ['resourceId'],
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 3, y: 6 },
        linkedField: 'dailyRevenue',
        formatType: 'custom',
        customComponent: DailyRevenue,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 0, y: 7 },
        linkedField: 'supplierId',
        linkedFieldForDefaultValue: 'supplier.name',
        type: 'number',
        width: 'w-full',
        formatType: 'dropdownWithSearch',
        moduleName: MN_SUPPLIERS,
        translationModuleName: MN_ORDERS,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 1, y: 7 },
        linkedField: 'typeOfPaymentSupplierId',
        linkedFieldForDefaultValue: 'typeOfPaymentSupplier.name',
        type: 'number',
        width: 'w-full',
        formatType: 'dropdownWithSearch',
        moduleName: MN_TYPE_OF_PAYMENTS,
        translationModuleName: MN_ORDERS,
        dependsOnFields: ['supplierId'],
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 0, y: 8 },
        linkedField: 'technology',
        type: 'string',
        width: 'w-full',
        formatType: 'input',
        moduleName: MN_ORDERS,
        translationModuleName: MN_ORDERS,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 1, y: 8 },
        linkedField: 'orderState',
        type: 'string',
        width: 'w-full',
        formatType: 'dropdown',
        enumValues: OrderStateEnum,
        moduleName: MN_ORDERS,
        translationModuleName: MN_ORDERS,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 0, y: 9 },
        linkedField: 'note',
        type: 'string',
        width: 'w-full',
        formatType: 'textArea',
        moduleName: MN_ORDERS,
        translationModuleName: MN_ORDERS,
      },
      {
        visible: true,
        readOnly: false,
        linkedField: 'CreateOrderDocButton',
        position: { x: 0, y: 10 },
        formatType: 'custom',
        customComponent: CreateOrderDocButton,
      },
      {
        visible: true,
        readOnly: false,
        linkedField: 'CreateOrderDuplicateButton',
        position: { x: 1, y: 10 },
        formatType: 'custom',
        customComponent: CreateOrderDuplicateButton,
      },
    ];

    // @ts-ignore
    this.formValidationSchema = z.object({
      id: z.number().nullable().optional(),
      jobId: z.number({
        invalid_type_error: 'fieldRequired',
        required_error: 'fieldRequired',
      }),
      typeOfPaymentId: z.number({
        invalid_type_error: 'fieldRequired',
        required_error: 'fieldRequired',
      }),
      typeOfPaymentSupplierId: z.number().nullable().optional(),
      code: z
        .string({
          invalid_type_error: 'fieldRequired',
          required_error: 'fieldRequired',
        })
        .min(2, { message: 'min_2' })
        .max(30, { message: 'max_30' }),
      description: z
        .string({
          invalid_type_error: 'fieldRequired',
          required_error: 'fieldRequired',
        })
        .min(2, { message: 'min_2' })
        .max(30, { message: 'max_30' }),
      startDate: z.string({
        invalid_type_error: 'fieldRequired',
        required_error: 'fieldRequired',
      }),
      endDate: z.string({
        invalid_type_error: 'fieldRequired',
        required_error: 'fieldRequired',
      }),
      resourceId: z.number({
        invalid_type_error: 'fieldRequired',
        required_error: 'fieldRequired',
      }),
      supplierId: z.number().nullable().optional(),
      hourCost: z
        .number()
        .nonnegative({ message: 'nonNegativeNumber' })
        .min(0, { message: 'min_num_0' })
        .nullable()
        .optional(),
      hourRevenue: z
        .number({
          invalid_type_error: 'fieldRequired',
          required_error: 'fieldRequired',
        })
        .nonnegative({ message: 'nonNegativeNumber' })
        .nullable()
        .optional(),
      hoursAllocatedPerDay: z
        .number({
          invalid_type_error: 'fieldRequired',
          required_error: 'fieldRequired',
        })
        .nonnegative({ message: 'nonNegativeNumber' })
        .max(16, { message: 'max_num_16' })
        .nullable()
        .optional(),
      fixedCost: z
        .union([
          z.number().nonnegative({ message: 'nonNegativeNumber' }),
          z.null(),
          z.undefined(),
        ])
        .optional(),
      orderType: z.string({
        invalid_type_error: 'fieldRequired',
        required_error: 'fieldRequired',
      }),
      orderState: z.string({
        invalid_type_error: 'fieldRequired',
        required_error: 'fieldRequired',
      }),
      technology: z
        .string({
          invalid_type_error: 'fieldRequired',
          required_error: 'fieldRequired',
        })
        .min(2, { message: 'min_2' })
        .max(30, { message: 'max_50' })
        .nullable()
        .optional(),
      note: z
        .string()
        .min(2, { message: 'min_2' })
        .max(255, { message: 'max_255' })
        .nullable()
        .optional(),
    });

    this.hook = HookComponentOrder;
  }
}

export default OrderRepository;
